import React, { useState } from 'react'
import { Card, Form, Input, Button, Alert, Typography, Space } from 'antd'
import { LockOutlined, MailOutlined, WarningOutlined, CheckCircleOutlined, DashboardOutlined, LoginOutlined } from '@ant-design/icons'
import { useAuthMfaSecure } from 'providers/AuthSecureManage/AuthMfaSecureManage'
import MfaSetup from './MfaSetup'
import MfaVerification from './MfaVerification'
import styles from './LoginSecure.module.css'

const { Title, Text } = Typography

enum LoginSecureStepEnum {
  LOGIN = 'login',
  SETUP = 'setup',
  VERIFY = 'verify',
  SUCCESS = 'success',
}

const LoginExperience = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState(LoginSecureStepEnum.LOGIN) // login, setup, verify, success
  const [userFactors, setUserFactors] = useState([])
  const [currentUser, setCurrentUser] = useState(null)
  const [challengeId, setChallengeId] = useState(null)
  const [userEmail, setUserEmail] = useState('')
  
  const {
    secureSignIn,
    listFactorsMFA,
    getAuthenticatorAssuranceLevel,
    createChallengeMFA,
    secureSignOut,
  } = useAuthMfaSecure()

  const handleLogin = async (values) => {
    try {
      setLoading(true)
      setError('')
      
      // Store the user's email for later use
      setUserEmail(values.email)

      const { user } = await secureSignIn(values.email, values.password)

      if (user) {
        setCurrentUser(user)

        const aalData = await getAuthenticatorAssuranceLevel();

        if (aalData.currentLevel === 'aal1') {
          // aal1 = solo password, necesita MFA
          if (aalData.nextLevel === 'aal2') {
            // Tiene factores MFA configurados - necesita verificación
            console.log('✅ Usuario CON MFA (aal1→aal2) - solicitando verificación OBLIGATORIA')

            const factors = await listFactorsMFA()
            setUserFactors(factors.totp || [])

            // Crear challenge inmediatamente para la verificación
            const challenge = await createChallengeMFA(factors.totp[0].id)
            setChallengeId(challenge.id)
            console.log('🎯 Challenge creado para verificación obligatoria:', challenge.id)

            setStep(LoginSecureStepEnum.VERIFY)
          } else {
            // NO tiene factores MFA configurados - FORZAR setup
            setStep(LoginSecureStepEnum.SETUP)
          }
        } else if (aalData.currentLevel === 'aal2') {
          // Ya tiene AAL2 - acceso completo
          setStep(LoginSecureStepEnum.SUCCESS)
        }
      }
    } catch (error) {
      console.error('Error en login:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleMFASetupComplete = async () => {
    console.log('MFA setup completado - redirigiendo a verificación obligatoria')
    
    // Recargar factores después del setup
    try {
      const factors = await listFactorsMFA();
      setUserFactors(factors.totp || [])
      
      // Crear challenge para verificación inmediata después del setup
      if (factors.totp && factors.totp.length > 0) {
        const challenge = await createChallengeMFA(factors.totp[0].id)
        setChallengeId(challenge.id)
        console.log('🎯 Challenge creado después del setup:', challenge.id)
      }
      
      setStep(LoginSecureStepEnum.VERIFY)
    } catch (error) {
      console.error('Error cargando factores después del setup:', error)
      setError('Error configurando verificación: ' + error.message)
    }
  }

  const handleMFAVerificationSuccess = async () => {
    
    let attempts = 0
    const maxAttempts = 10
    
    while (attempts < maxAttempts) {
      try {
        const aalData = await getAuthenticatorAssuranceLevel()
        if (aalData.currentLevel === 'aal2') {
          console.log('Sesión AAL2 confirmada, redirigiendo...')
          window.location.href = '/dashboard'
          return
        }
        console.log('Esperando actualización de sesión a AAL2... intento', attempts + 1)
        await new Promise(resolve => setTimeout(resolve, 300))
        attempts++
      } catch (error) {
        console.error('Error verificando AAL:', error)
        attempts++
        await new Promise(resolve => setTimeout(resolve, 300))
      }
    }
    
    console.log('Timeout esperando AAL2, redirigiendo de todas formas...')
    window.location.href = '/dashboard'
  }

  const handleBackToLogin = async () => {
    try {
      await secureSignOut()
    } catch (error) {
      console.error('Error al limpiar sesión:', error)
    }
    
    setStep(LoginSecureStepEnum.LOGIN)
    setError('')
    setCurrentUser(null)
    setUserFactors([])
    setChallengeId(null)
    setUserEmail('')
  }

  // Paso 1: Login Form
  if (step === 'login') {
    return (
      <div className={styles.container}>
        <Card style={{ maxWidth: 500, width: '100%' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <LockOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
              <Title level={2} style={{ marginBottom: '8px' }}>Login</Title>

            </div>
            {error && (
              <Alert
                message="Error de autenticación"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError('')}
              />
            )}

            <Form
              name="login"
              layout="vertical"
              onFinish={handleLogin}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                label="Correo electrónico"
                name="email"
                rules={[
                  { required: true, message: 'Por favor ingrese su correo electrónico' },
                  { type: 'email', message: 'Ingrese un correo electrónico válido' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="usuario@ejemplo.com"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label="Contraseña"
                name="password"
                rules={[{ required: true, message: 'Por favor ingrese su contraseña' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="••••••••"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  icon={!loading && <LoginOutlined />}
                >
                  {loading ? 'Verificando...' : 'Iniciar Sesión'}
                </Button>
              </Form.Item>
            </Form>

            <Text type="secondary" style={{ textAlign: 'center', display: 'hidden', fontSize: '12px' }}>
              Al continuar, acepta configurar autenticación de dos factores si no la tiene habilitada.
            </Text>
          </Space>
        </Card>
      </div>
    )
  }

  // Paso 2: Forzar Setup MFA
  if (step === 'setup') {
    return (
      <MfaSetup
        user={currentUser}
        onComplete={handleMFASetupComplete}
        onCancel={handleBackToLogin}
      />
    )
  }

  // Paso 3: Verificación MFA Obligatoria
  if (step === 'verify') {
    return (
      <MfaVerification
        factors={userFactors}
        challengeId={challengeId}
        userEmail={userEmail}
        onSuccess={handleMFAVerificationSuccess}
        onCancel={handleBackToLogin}
      />
    )
  }

  // Paso 4: Acceso Concedido
  if (step === 'success') {
    return (
      <div className={styles.container}>
        <Card style={{ maxWidth: 500, width: '100%' }}>
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <div>
              <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }} />
              <Title level={2} style={{ color: '#52c41a', marginBottom: '8px' }}>
                Acceso Concedido
              </Title>
              <Title level={4} type="secondary" style={{ fontWeight: 'normal' }}>
                Autenticación exitosa con MFA
              </Title>
            </div>

            <Alert
              message="Sesión segura establecida"
              type="success"
              icon={<LockOutlined />}
              showIcon
            />

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Button
                type="primary"
                size="large"
                block
                icon={<DashboardOutlined />}
                onClick={() => window.location.href = '/dashboard'}
              >
                Ir al Dashboard
              </Button>

              <Button
                size="large"
                block
                icon={<LoginOutlined />}
                onClick={handleBackToLogin}
              >
                Nuevo Login
              </Button>
            </Space>
          </Space>
        </Card>
      </div>
    )
  }

  return null
}

export default LoginExperience